import { useEffect, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setAccessTokenGetter, setSessionExpiredHandler } from '@/api/client';

/** Key read after login to return the user to where they were. */
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

/**
 * Injects the Auth0 access-token getter into the shared Axios client so every
 * API call carries a Bearer token without React coupling in the API layer.
 *
 * The getter is invoked by the request interceptor on *every* call. Auth0's
 * `getAccessTokenSilently()` returns the cached token while it is valid and
 * silently refreshes it once expired, so the user never sends a stale token.
 * When silent refresh is impossible (Auth0 session gone) the API client calls
 * the session-expired handler, which sends the user through login again and
 * brings them back to the page they were on.
 */
export function AuthTokenBridge({ children }: { children: ReactNode }) {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  useEffect(() => {
    // While Auth0 is still restoring the session from cache, or the user is
    // signed out, requests go unauthenticated (public endpoints / guards
    // block the rest).
    if (isLoading || !isAuthenticated) {
      setAccessTokenGetter(undefined);
      return;
    }

    setAccessTokenGetter(({ forceRefresh } = {}) =>
      getAccessTokenSilently({
        // 'off' bypasses the cache and always hits Auth0 — used by the 401
        // retry path. 'on' (default) returns the cached token when still valid
        // and refreshes it otherwise.
        cacheMode: forceRefresh ? 'off' : 'on',
      }),
    );

    return () => setAccessTokenGetter(undefined);
  }, [getAccessTokenSilently, isAuthenticated, isLoading]);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      const returnTo = window.location.pathname + window.location.search;
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, returnTo);
      void loginWithRedirect({ appState: { returnTo } });
    });
    return () => setSessionExpiredHandler(undefined);
  }, [loginWithRedirect]);

  return <>{children}</>;
}
